/**
 * Custom hook to manage the react-dnd drop logic for a mobile control.
 * It calculates the drop indicator position and calls the moveControl action.
 */
import { useRef } from 'react';
import { useDrop } from 'react-dnd';

export const useControlDrop = ({ control, index, sectionId, dropIndicator, setDropIndicator, moveControl, activePageId }) => {
    const ref = useRef(null);

    const [{ isOver }, drop] = useDrop({
        accept: ['CONTROL', 'MOBILE_OBJECT'],
        hover(item, monitor) {
            if (!ref.current || item.id === control.id) return;

            // Determine position of drop indicator (top or bottom half of the item)
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            const position = hoverClientY < hoverMiddleY ? 'top' : 'bottom';

            // Set the drop indicator state in the parent if it has changed
            if (dropIndicator?.targetId !== control.id || dropIndicator?.position !== position) {
                setDropIndicator({ targetId: control.id, position });
            }
        },
        drop(item, monitor) {
            if (!dropIndicator) return;

            const dragIndex = item.index;
            const hoverIndex = index;
            const position = dropIndicator.position;
            const isSameSection = item.sectionId === sectionId;

            // Calculate target index based on drop position
            let targetIndex = position === 'top' ? hoverIndex : hoverIndex + 1;

            // Adjust index when moving an item downwards within the same section
            if (isSameSection && dragIndex < targetIndex) {
                targetIndex--;
            }

            // Prevent dropping in the same spot
            if (isSameSection && targetIndex === dragIndex) {
                setDropIndicator(null);
                return;
            }

            // Execute the move action
            moveControl({
                pageId: activePageId,
                controlId: item.id,
                fromSectionId: item.sectionId,
                fromIndex: dragIndex,
                toSectionId: sectionId,
                toIndex: targetIndex,
            });

            setDropIndicator(null);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    // Attach the drop ref to our component's ref
    drop(ref);

    // Determine if the drop indicator should be shown for this specific component
    const showIndicator = isOver && dropIndicator?.targetId === control.id ? dropIndicator.position : null;

    return { ref, showIndicator };
};